import { PageController } from '@client/components/_core/layout/PageController';
import { TableToolbar } from '@client/components/_core/table/TableToolbar';
import { Button } from '@client/components/_ui/data-entry/ButtonModern';
import { ComponentPreview } from '@client/components/_ui/display/ComponentPreview';
import { Indicator } from '@client/components/_ui/display/Indicator';
import { TaggedList } from '@client/components/_ui/display/TaggedList';
import { ControlFrequencyTag } from '@client/components/_ui/display/tags';
import { User } from '@client/components/_ui/display/User';
import { Space } from '@client/components/_ui/layout/Space';
import { ControlFilters } from '@client/components/controls/ControlFilters';
import { useOrgId } from '@client/hooks/business/org/useOrgId';
import { useNavigate } from '@client/hooks/navigation/useNavigate';
import { usePathState } from '@client/hooks/navigation/usePathState';
import { createColumnHelper, PrismaTable, type PrismaTableColumn, usePrismaTable } from '@client/hooks/table-v2';
import { type RouterInputs, type RouterOutputs, trpc } from '@client/trpc/client';
import { getHighestPriorityStatus } from '@client/utils';
import {
    CONTROL_STATUSES,
    CONTROL_TYPES,
    type ControlFrequency,
    type ControlStatus,
    type ControlType,
    DEFAULT_PAGE_SIZE,
} from '@shared/constants';
import { FilterOperatorSchema } from '@shared/schemas/filter';
import { createFileRoute } from '@tanstack/react-router';
import { zodValidator } from '@tanstack/zod-adapter';
import { z } from 'zod';

const ControlSearchSchema = z.object({
    'control-filters': z
        .array(
            z.union([
                z.object({
                    id: z.string(),
                    filterId: z.literal('owner'),
                    operator: FilterOperatorSchema,
                    value: z.coerce.bigint(),
                }),
                z.object({
                    id: z.string(),
                    filterId: z.literal('handling-party-ids'),
                    operator: FilterOperatorSchema,
                    value: z.array(z.coerce.bigint()),
                }),
                z.object({
                    id: z.string(),
                    filterId: z.literal('type'),
                    operator: FilterOperatorSchema,
                    value: z.string(),
                }),
                z.object({
                    id: z.string(),
                    filterId: z.string(),
                    operator: FilterOperatorSchema,
                    value: z.any(),
                }),
            ]),
        )
        .optional()
        .catch(undefined),
});

export const Route = createFileRoute('/console/$orgId/controls/register/')({
    staticData: {
        ui: {
            title: 'Control register',
            subtitle: 'Manage and track your risk controls',
        },
    },
    validateSearch: zodValidator(ControlSearchSchema),
    component: ControlsRegisterSearch,
});

type QueryParams = RouterInputs['control']['listControls'];
type ControlRow = NonNullable<RouterOutputs['control']['listControls']>['rows'][number];
type ControlOrderBy = NonNullable<QueryParams['orderBy']>;

function useControlTableColumns(orgId: bigint): readonly PrismaTableColumn<ControlRow, ControlOrderBy, any>[] {
    const columnHelper = createColumnHelper<ControlRow, ControlOrderBy>();

    return [
        columnHelper.display({
            id: 'indicator',
            header: '',
            cell: ({ row: { status, ControlTests } }) => {
                const { color, title } = getHighestPriorityStatus(status, ControlTests);

                return (
                    <div className="flex items-center justify-center">
                        <Indicator
                            tooltip={{
                                title,
                            }}
                            style={{
                                background: color,
                            }}
                        />
                    </div>
                );
            },
            width: 40,
            fixed: 'left',
        }),
        columnHelper.accessor('id', {
            id: 'controlNo',
            header: 'Control no.',
            sortable: 'id',
            cell: ({ row }) => String(row.id),
        }),
        columnHelper.accessor('name', {
            id: 'name',
            header: 'Name',
            sortable: 'name',
            cell: ({ row }) => <ComponentPreview>{row.name}</ComponentPreview>,
            className: 'max-w-xs align-top',
        }),
        columnHelper.accessor('objective', {
            id: 'objective',
            header: 'Objective',
            sortable: 'objective',
            cell: ({ row }) => <ComponentPreview>{row.objective}</ComponentPreview>,
            className: 'max-w-xs align-top',
        }),
        columnHelper.accessor('status', {
            id: 'status',
            header: 'Status',
            sortable: 'status',
            cell: ({ row }) => CONTROL_STATUSES[row.status as ControlStatus] || row.status,
        }),
        columnHelper.accessor('frequency', {
            id: 'frequency',
            header: 'Control frequency',
            sortable: 'frequency',
            cell: ({ row }) => <ControlFrequencyTag value={row.frequency as ControlFrequency | null} />,
        }),
        columnHelper.display({
            id: 'handlingParties',
            header: 'Handling parties',
            cell: ({ row }) => (
                <TaggedList
                    items={row.HandlingParties || []}
                    accessor={(item) => item.HandlingParty.name}
                    additionalItemsTitle="Additional handling parties:"
                    displayLimit={3}
                    tagProps={{
                        className: 'text-sm',
                        maxWidth: '150px',
                    }}
                    emptyText=""
                />
            ),
        }),
        columnHelper.accessor('type', {
            id: 'type',
            header: 'Type',
            sortable: 'type',
            cell: ({ row }) => (row.type ? CONTROL_TYPES[row.type as ControlType] || row.type : ''),
        }),
        columnHelper.accessor('ownerId', {
            id: 'owner',
            header: 'Owner',
            sortable: 'ownerId',
            cell: ({ row }) => <User userId={row.ownerId} />,
        }),
        columnHelper.actions({
            cell: ({ row: value }) => (
                <Space>
                    <Button.OpenInNewTab
                        to="/console/$orgId/controls/register/details/$id"
                        params={{
                            orgId,
                            id: value.id,
                        }}
                    />
                </Space>
            ),
        }),
    ] as const;
}

function ControlsRegisterSearch() {
    const orgId = useOrgId();
    const navigate = useNavigate();
    const searchParams = Route.useSearch();

    const [input, setInput] = usePathState<QueryParams>('query-params', {
        where: {},
        orderBy: { id: 'desc' },
        limit: DEFAULT_PAGE_SIZE,
        offset: 0,
    });

    const { data, isPending } = trpc.control.listControls.useQuery(input);

    const columns = useControlTableColumns(orgId);

    const tableProps = usePrismaTable({
        data: data?.rows || [],
        total: data?.total || 0,
        loading: isPending,
        columns,
        rowKey: 'id',
        queryParams: input,
        onParamsChange: setInput,
        pagination: {
            position: 'both',
            showSizeChanger: true,
        },
        columnVisibility: {
            enabled: true,
            persistKey: 'control-register-table-columns',
            enableReordering: true,
        },
    });

    return (
        <PageController route={Route}>
            <TableToolbar
                filters={
                    <ControlFilters
                        initialFilters={searchParams['control-filters']}
                        onChange={(where) => setInput((prev) => ({ ...prev, where }))}
                    />
                }
                actions={
                    <Button.Add.Link
                        label="Add control"
                        to="/console/$orgId/controls/register/add"
                        params={{ orgId }}
                        variant="primary"
                    />
                }
            />

            <PrismaTable
                {...tableProps}
                onRow={({ id }) => ({
                    className: 'cursor-pointer',
                    onClick: (e) =>
                        navigate(
                            {
                                to: '/console/$orgId/controls/register/details/$id',
                                params: {
                                    orgId,
                                    id,
                                },
                            },
                            e,
                        ),
                })}
            />
        </PageController>
    );
}
